import { motion } from 'framer-motion';
import { MapPin, Sparkles, ChevronRight, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatPrice } from '../lib/money';
import { SalonSearchResult } from '../api/use-marketplace';
import { formatAddress, subdomainHashHue } from './site-shell';

function branchCity(salon: SalonSearchResult): string {
  const raw = salon.location?.address;
  if (!raw) return '';
  try {
    const a = JSON.parse(raw) as { city?: string };
    return a.city?.trim() ?? '';
  } catch {
    return '';
  }
}

/**
 * Marketplace salon card — matches the Treatwell/Fresha pattern:
 *   image + badges → name row → location → services preview → footer
 *
 * Sans-serif throughout. Featured pill uses the brand cognac as accent;
 * the rest is graphite/charcoal so the card reads as a modern listing
 * rather than an editorial card.
 *
 * No fake ratings/review counts/discounts — those are honest gaps until
 * we have real data.
 */
export function SalonCard({ salon }: { salon: SalonSearchResult }) {
  const { t } = useTranslation();
  const hash = subdomainHashHue(salon.subdomain);
  return (
    <motion.div
      whileHover={{ y: -4 }}
      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      className="h-full"
    >
      <div className="group flex flex-col h-full rounded-2xl overflow-hidden bg-card border border-border/60 transition-all duration-300 hover:shadow-xl hover:border-foreground/20">
        {/* Cover */}
        <Link
          to={`/salon/${salon.subdomain}`}
          className="relative block w-full aspect-[16/10] overflow-hidden gradient-placeholder"
          style={{ ['--gp-h' as never]: hash }}
        >
          {salon.coverImageUrl && (
            <img
              src={salon.coverImageUrl}
              alt={salon.name}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          )}
          {salon.featured && (
            <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-foreground text-background text-[11px] font-semibold tracking-wide">
              <Sparkles className="w-3 h-3" />
              {t('salon.featured')}
            </span>
          )}
          {salon.distanceKm != null && (
            <span className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-background/90 backdrop-blur-sm text-foreground text-[11px] font-semibold border border-border/40 tabular-nums">
              <MapPin className="w-3 h-3" />
              {salon.distanceKm.toFixed(1)} km
            </span>
          )}
        </Link>

        {/* Body */}
        <div className="flex flex-col flex-1 p-5">
          <div className="flex items-start justify-between gap-3 mb-1">
            <Link
              to={`/salon/${salon.subdomain}`}
              className="font-semibold text-lg text-foreground leading-tight hover:text-primary transition-colors flex items-center gap-1.5"
            >
              {salon.name}
              {branchCity(salon) && (
                <span className="text-muted-foreground font-normal"> — {branchCity(salon)}</span>
              )}
              <Check className="w-4 h-4 text-primary shrink-0" aria-label={t('search.card.verified')} />
            </Link>
          </div>

          {salon.isMobile && (
            <span className="inline-block bg-primary/10 text-primary text-xs px-2 py-0.5 rounded-full mb-2">
              {t('search.chip.mobileArtist')}
            </span>
          )}

          {salon.serviceArea && (
            <p className="text-xs text-muted-foreground mb-4">
              {t('search.serviceAreaPrefix')} {salon.serviceArea}
            </p>
          )}

          {salon.location?.address && (
            <p className="flex items-center gap-1.5 text-sm text-muted-foreground mb-4">
              <MapPin className="w-3.5 h-3.5 shrink-0" />
              <span className="truncate">{formatAddress(salon.location.address)}</span>
            </p>
          )}

          {salon.topServices && salon.topServices.length > 0 && (
            <div className="space-y-2 mb-5">
              {salon.topServices.slice(0, 2).map((svc) => (
                <div
                  key={svc.name}
                  className="flex items-center justify-between text-sm"
                >
                  <span className="text-muted-foreground truncate mr-3">
                    {svc.name}
                  </span>
                  <span className="font-semibold text-foreground tabular-nums whitespace-nowrap">
                    {formatPrice(svc.price, salon.currency)}
                  </span>
                </div>
              ))}
              {salon.topServices.length > 2 && (
                <p className="text-xs text-muted-foreground">
                  {t('search.card.moreServices', {
                    count: salon.topServices.length - 2,
                  })}
                </p>
              )}
            </div>
          )}

          {/* Footer pushed to bottom of card so heights line up */}
          <div className="mt-auto flex gap-2 pt-2">
            <Link
              to={`/salon/${salon.subdomain}`}
              className="flex-1 inline-flex items-center justify-center px-4 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm font-medium hover:bg-secondary transition-colors"
            >
              {t('search.card.viewDetails')}
            </Link>
            <Link
              to={`/salon/${salon.subdomain}`}
              className="flex-1 inline-flex items-center justify-center gap-1 px-4 py-2.5 rounded-xl bg-foreground text-background text-sm font-medium hover:bg-foreground/90 transition-colors"
            >
              {t('search.card.bookNow')}
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export function SalonCardSkeleton() {
  return (
    <div className="rounded-2xl border border-border/60 bg-card overflow-hidden">
      <div className="aspect-[16/10] bg-muted/60 animate-pulse" />
      <div className="p-5 space-y-3">
        <div className="h-5 w-2/3 bg-muted/60 rounded animate-pulse" />
        <div className="h-4 w-1/2 bg-muted/60 rounded animate-pulse" />
        <div className="h-9 w-full bg-muted/40 rounded-xl animate-pulse mt-4" />
      </div>
    </div>
  );
}
